/**
 * Clipboard utilities for copying rich text (HTML) and plain text.
 */

/**
 * Copy HTML content to clipboard as rich text.
 * This allows pasting into email clients (Gmail, Outlook) with formatting preserved.
 */
export async function copyHtmlToClipboard(html: string): Promise<boolean> {
  try {
    // Modern Clipboard API with HTML support
    if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
      const blob = new Blob([html], { type: 'text/html' });
      const plainBlob = new Blob([stripHtml(html)], { type: 'text/plain' });

      const clipboardItem = new ClipboardItem({
        'text/html': blob,
        'text/plain': plainBlob,
      });

      await navigator.clipboard.write([clipboardItem]);
      return true;
    }

    // Fallback: Create a temporary element and use execCommand
    return copyHtmlFallback(html);
  } catch (error) {
    console.error('Failed to copy HTML to clipboard:', error);
    // Try fallback on error
    return copyHtmlFallback(html);
  }
}

/**
 * Fallback method for copying HTML using execCommand.
 */
function copyHtmlFallback(html: string): boolean {
  try {
    // Create a temporary container
    const container = document.createElement('div');
    container.innerHTML = html;
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    document.body.appendChild(container);

    // Select the content
    const range = document.createRange();
    range.selectNodeContents(container);

    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }

    // Copy
    const success = document.execCommand('copy');

    // Cleanup
    if (selection) {
      selection.removeAllRanges();
    }
    document.body.removeChild(container);

    return success;
  } catch (error) {
    console.error('Fallback copy failed:', error);
    return false;
  }
}

/**
 * Copy plain text to clipboard.
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback using execCommand
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    return success;
  } catch (error) {
    console.error('Failed to copy text to clipboard:', error);
    return false;
  }
}

/**
 * Strip HTML tags from a string to get plain text.
 */
export function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}
